import React from 'react';
import { withStyles } from '@material-ui/core';
import styles from './styles';
import { compose } from 'recompose';
import WithDrawer from '../../../../Dispatch/Common/Drawer/WithDrawer';
import { withSnackbar } from 'notistack';
import { DateTimeEditor } from '@dataGrid/components/cellEditors';
import { convertDateTimeToTimeString, isTimeValid } from '@dataGrid/gridUtilities';
import CustomTextField from '../../../../Dispatch/RouteExecution/DriverPopUp/common/CustomTextField';
import { postData } from '../../../../../../api/componentAPIs';
import ActionButton from './ActionButton';
import appConstants from '../../../../../../appConstants';
import {
  MAX_DRIVER_COUNT,
  MIN_DRIVER_COUNT,
  getDefaultDriverPreferenceInfo,
  getFieldError,
  getErrorInfo,
  getDayOfWeekCounts
} from './utils';
import WeekdayDriverCountInput from './DayOfWeekDriverCountInput';

class AddEditDriverPreference extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errorMessage: null,
      errorInfo: {},
      updateInProgress: false,
      driverPreferenceInfo: getDefaultDriverPreferenceInfo(this.props.driverPreferenceInfo),
      toggleState: {
        dayOfWeek: false,
        weekend: false
      }
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.driverPreferenceInfo &&
      this.props.driverPreferenceInfo.startTime !== prevProps.driverPreferenceInfo?.startTime
    ) {
      this.setFreshDriverPreferenceInfo(this.props.driverPreferenceInfo);
    }
  }

  setFreshDriverPreferenceInfo = (selectedDriverPreferenceInfo) => {
    const driverPreferenceInfo = getDefaultDriverPreferenceInfo(selectedDriverPreferenceInfo);
    this.setState({
      driverPreferenceInfo,
      errorInfo: {}
    });
  };

  saveDriverPreference = () => {
    const errorInfo = getErrorInfo(this.state.driverPreferenceInfo, this.state.toggleState);
    this.setState({ errorInfo });
    const hasErrors = Object.values(errorInfo).some((value) => value);
    if (hasErrors) {
      return;
    }

    const dayOfWeekCounts = getDayOfWeekCounts(this.state.driverPreferenceInfo);

    const updateObject = {
      scenarioId: this.props.userData.userProfile.scenarioID,
      terminalId: this.props.userData.userProfile.terminal.id,
      startTimeChangedFrom: this.props.driverPreferenceInfo?.startTime ?? null,
      startTimeChangedTo: this.state.driverPreferenceInfo.startTime,
      defaultDriverCount: this.state.driverPreferenceInfo.defaultDriverCount || 0,
      ...dayOfWeekCounts
    };

    postData('UpdateGroupedDriverPreferences', updateObject)
      .then((response) => {
        if (response && response.status >= 200 && response.status < 400) {
          this.handleSuccess();
          return;
        }
        this.handleError(response.status);
      })
      .catch(() => {
        this.handleError();
      });
  };

  handleSuccess = () => {
    this.props.enqueueSnackbar(
      `${this.state.driverPreferenceInfo.startTime} ${
        this.props.driverPreferenceInfo ? 'updated' : 'created'
      } successfully`,
      { variant: 'success' }
    );
    this.props.getDriverPreferenceData();
    this.props.onClose();
  };

  handleError = (statusCode) => {
    if (statusCode === 403) {
      return;
    }
    this.props.enqueueSnackbar(
      `Failed to ${
        this.props.driverPreferenceInfo ? 'update' : 'create'
      } ${this.state.driverPreferenceInfo.startTime}`,
      { variant: 'error' }
    );
  };

  handleChange = (name) => (value) => {
    this.setState((state) => ({
      driverPreferenceInfo: {
        ...state.driverPreferenceInfo,
        [name]: value
      },
      errorInfo: {
        ...state.errorInfo,
        [name]: getFieldError(name, value, this.state.toggleState)
      },
      errorMessage: null
    }));
  };

  updateStartTime = (startTime) => {
    const { selectDriverPreferenceOnForm, driverData } = this.props;
    const { driverPreferenceInfo } = this.state;
    if (!startTime && driverPreferenceInfo.startTime !== startTime) {
      this.handleChange('startTime')(null);
      return;
    }
    const timeString = convertDateTimeToTimeString(startTime);
    if (isTimeValid(timeString)) {
      const existingStartTime = driverData?.find((driver) => driver.startTime === timeString);
      if (existingStartTime) {
        selectDriverPreferenceOnForm([existingStartTime], existingStartTime);
      } else {
        this.setFreshDriverPreferenceInfo({ startTime: timeString }); // Setting the new driver preference info to the local state with the new startTime value
        selectDriverPreferenceOnForm([], null); // Reset parent state to prevent old driver preference from being passed as props
        this.handleChange('startTime')(timeString);
      }
    }
  };

  handleToggleState = (state) => {
    this.setState((prevState) => ({
      toggleState: { ...prevState.toggleState, ...state },
      errorInfo: {}
    }));
  };

  render() {
    const { classes } = this.props;
    const { updateInProgress, driverPreferenceInfo, errorInfo, errorMessage, toggleState } =
      this.state;

    return (
      <>
        <div>
          <header className={classes.moduleHeader}>
            {this.props.driverPreferenceInfo ? 'Update Driver Preference' : 'Add Driver Preference'}
          </header>
          <div className={classes.formContainer}>
            <div className={classes.formValue}>
              <div
                className={
                  errorInfo?.startTime
                    ? classes.editorContainerTimeError
                    : classes.editorContainerTime
                }>
                <DateTimeEditor
                  dateType={appConstants.DATETYPES.TIME}
                  InputLabelProps={{
                    classes: {
                      root: classes.labelProps,
                      filled: classes.filledLabel,
                      shrink: classes.shrink
                    }
                  }}
                  inputRootClass={classes.timeEditorInput}
                  label="Start Time"
                  value={driverPreferenceInfo?.startTime}
                  onChange={this.updateStartTime}
                  variant="filled"
                  disableFocus
                  enablePickerPortal
                  disableFocusInput
                  inputFocusedClass={classes.timeEditorInputFocused}
                />
                {Boolean(errorInfo?.startTime) && (
                  <p
                    className={`MuiFormHelperText-root MuiFormHelperText-error ${classes.errorMessageVisible}`}>
                    {errorInfo.startTime}
                  </p>
                )}
              </div>
            </div>
            <div className={classes.formValue}>
              <CustomTextField
                id="defaultDriverCount"
                handleChange={this.handleChange('defaultDriverCount')}
                value={driverPreferenceInfo?.defaultDriverCount ?? ''} // ?? is used to handle null values
                error={errorInfo?.defaultDriverCount}
                label="Default Driver Count"
                type="number"
                inputProps={{ max: MAX_DRIVER_COUNT, min: MIN_DRIVER_COUNT }}
                disabled={toggleState.dayOfWeek}
              />
            </div>
            <WeekdayDriverCountInput
              key={driverPreferenceInfo?.startTime} // key is used to force re-render the component when the startTime changes
              classes={classes}
              driverPreferenceInfo={driverPreferenceInfo}
              errorInfo={errorInfo}
              handleChange={this.handleChange}
              toggleState={toggleState}
              handleToggleState={this.handleToggleState}
            />
            {Boolean(errorMessage) && (
              <p
                className={`MuiFormHelperText-root MuiFormHelperText-error ${classes.errorMessageVisible}`}>
                {errorMessage}
              </p>
            )}
          </div>
          <ActionButton
            classes={classes}
            onClose={this.props.onClose}
            saveDriverPreference={this.saveDriverPreference}
            updateInProgress={updateInProgress}
            errorMessage={errorMessage}
            driverPreferenceInfo={this.props.driverPreferenceInfo}
          />
        </div>
      </>
    );
  }
}

export default compose(WithDrawer, withSnackbar, withStyles(styles))(AddEditDriverPreference);
